import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { PMField } from "../../utils/PMField";
import PMSwitch from "../../utils/PMSwitch";
import { PMNotice } from "../../utils/notifications";
import {
  createWorkOrderSalesOrder,
  isPromangApiErrorResponse,
  markWorkOrderReadyForInvoicing,
  promangApiErrorsMessage,
  promangApiErrorsMessageFromCaught,
} from "../../../api/work-order";

function OrderDialogSection({
  title,
  description,
  headerRight,
  children,
  contentClassName = "space-y-4",
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1 pr-2">
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-slate-600">{description}</p>
          ) : null}
        </div>
        {headerRight ? (
          <div className="shrink-0 self-start sm:self-center">{headerRight}</div>
        ) : null}
      </div>
      <div className={contentClassName}>{children}</div>
    </section>
  );
}

function stripHtml(text) {
  if (!text || typeof text !== "string") return "";
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(text, "text/html");
  return htmlDoc.body.textContent || "";
}

export function WorkOrderBillingReadyDialog({
  open,
  onClose,
  workOrder,
  hours = [],
  onSaved,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const formatHourDecimal = (value) => {
    const s = Number(value || 0).toFixed(1);
    const lang = i18n.resolvedLanguage || i18n.language || "";
    if (lang === "nb" || lang === "no" || lang.startsWith("nb")) {
      return s.replace(".", ",");
    }
    return s;
  };

  const draft = workOrder?.invoice_draft || {};
  const billableHours = useMemo(
    () => (Array.isArray(hours) ? hours : []).filter((h) => h && h.id),
    [hours]
  );
  const usageItems = useMemo(() => {
    const items = workOrder?.items || [];
    return items.filter((it) => Number(it.quantity_used || 0) > 0);
  }, [workOrder?.items]);

  const [selectedHours, setSelectedHours] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [hoursSettings, setHoursSettings] = useState({
    discount: 0,
    manualQuantity: null,
    roundingMode: "actual",
    hourLines: "each",
  });

  useEffect(() => {
    if (!open || !workOrder?.id) return;

    const hs = draft.hours_settings || draft["hours_settings"] || {};
    setHoursSettings({
      discount: Number(hs.discount ?? hs["discount"] ?? 0) || 0,
      manualQuantity:
        hs.manualQuantity ?? hs["manualQuantity"] ?? null,
      roundingMode: hs.roundingMode || hs["roundingMode"] || "actual",
      hourLines: hs.hourLines || hs["hourLines"] || "each",
    });

    const savedH = draft.selected_hour_ids || draft["selected_hour_ids"];
    if (Array.isArray(savedH) && savedH.length > 0) {
      setSelectedHours(savedH.map(Number));
    } else if (billableHours.length > 0) {
      setSelectedHours(billableHours.map((h) => h.id));
    } else {
      setSelectedHours([]);
    }

    const savedI = draft.selected_item_ids || draft["selected_item_ids"];
    if (Array.isArray(savedI) && savedI.length > 0) {
      setSelectedItems(savedI.map(Number));
    } else if (usageItems.length > 0) {
      setSelectedItems(usageItems.map((i) => i.id));
    } else {
      setSelectedItems([]);
    }
  }, [
    open,
    workOrder?.id,
    workOrder?.invoice_draft,
    billableHours,
    usageItems,
  ]);

  const defaultTitle = useMemo(() => {
    const num = workOrder?.number;
    const title = workOrder?.title;
    if (num && title) return `${num} – ${title}`;
    return title || (num ? String(num) : "");
  }, [workOrder?.number, workOrder?.title]);

  const defaultDescription = useMemo(() => {
    const plain = stripHtml(workOrder?.description || "").trim();
    if (plain.length > 20) return plain.slice(0, 8000);
    return plain;
  }, [workOrder?.description]);

  const initialValues = useMemo(
    () => ({
      invoice_title:
        draft.invoice_title ||
        draft["invoice_title"] ||
        defaultTitle ||
        "",
      invoice_description:
        draft.invoice_description ||
        draft["invoice_description"] ||
        defaultDescription ||
        "",
      include_hours:
        draft.include_hours ?? draft["include_hours"] ?? billableHours.length > 0,
      include_items:
        draft.include_items ?? draft["include_items"] ?? usageItems.length > 0,
      add_tilleggsarbeider_group:
        draft.add_tilleggsarbeider_group ??
        draft["add_tilleggsarbeider_group"] ??
        true,
    }),
    [draft, defaultTitle, defaultDescription, billableHours.length, usageItems.length]
  );

  const selectedTotalHours = () => {
    if (!billableHours.length || !selectedHours.length) return 0;
    const hoursRows = billableHours.filter((h) => selectedHours.includes(h.id));
    return hoursRows.reduce((a, h) => a + Number(h.time || 0), 0);
  };

  const applyHoursRoundingPreview = (quantity, mode) => {
    const q = Number(quantity) || 0;
    if (q <= 0) return 0;
    const m = mode || "actual";
    switch (m) {
      case "actual":
      case "none":
        return Math.round(q * 1e6) / 1e6;
      case "ceil_half":
        return Math.ceil(q * 2) / 2;
      case "ceil_hour":
        return Math.ceil(q);
      case "nearest_half":
        return Math.round(q * 2) / 2;
      case "nearest_hour":
        return Math.round(q);
      default:
        return q;
    }
  };

  const calculateHoursQuantity = () => {
    const mode = hoursSettings.roundingMode || "actual";
    if (
      hoursSettings.manualQuantity !== null &&
      hoursSettings.manualQuantity !== ""
    ) {
      return applyHoursRoundingPreview(
        parseFloat(String(hoursSettings.manualQuantity).replace(",", ".")),
        mode
      );
    }
    return applyHoursRoundingPreview(selectedTotalHours(), mode);
  };

  const isReadyStatus = workOrder?.status === "ready_invoice";
  const hasLinkedOrder = Boolean(workOrder?.order?.id);

  const buildBillingPayload = (values) => ({
    invoice_title: values.invoice_title,
    invoice_description: values.invoice_description,
    include_hours: values.include_hours,
    include_items: values.include_items,
    add_tilleggsarbeider_group: values.add_tilleggsarbeider_group,
    selected_hour_ids: values.include_hours ? selectedHours : [],
    selected_item_ids: values.include_items ? selectedItems : [],
    hours_settings: values.include_hours ? hoursSettings : null,
  });

  const handleSaveReadyOnly = async (values) => {
    setSubmitting(true);
    try {
      const payload = buildBillingPayload(values);
      const res = await markWorkOrderReadyForInvoicing(workOrder.id, payload);
      if (isPromangApiErrorResponse(res)) {
        toast.error(
          promangApiErrorsMessage(res, t("work_orders.billing_ready.failed"))
        );
        return;
      }
      const wo = res?.data?.data ?? res?.data;
      toast.success(t("work_orders.billing_ready.success"));
      onSaved?.(wo);
      onClose();
    } catch (err) {
      toast.error(
        promangApiErrorsMessageFromCaught(
          err,
          t("work_orders.billing_ready.failed")
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSalesOrder = async (values) => {
    setSubmitting(true);
    try {
      const payload = buildBillingPayload(values);
      const res = await createWorkOrderSalesOrder(workOrder.id, payload);
      if (isPromangApiErrorResponse(res)) {
        toast.error(
          promangApiErrorsMessage(res, t("work_orders.billing_ready.failed_order"))
        );
        return;
      }
      const wo = res?.data?.data;
      const order = res?.data?.order;
      toast.success(t("work_orders.billing_ready.success_order"));
      if (wo) onSaved?.(wo);
      onClose();
      if (order?.id) {
        navigate(`/dashboard/orders/${order.id}`);
      }
    } catch (err) {
      toast.error(
        promangApiErrorsMessageFromCaught(
          err,
          t("work_orders.billing_ready.failed_order")
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!workOrder?.id) return null;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 max-sm:flex max-sm:flex-col max-sm:overflow-hidden sm:overflow-y-auto">
          <div className="flex min-h-full flex-1 justify-center p-0 text-center max-sm:min-h-0 max-sm:flex-1 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex max-h-[100dvh] w-full max-w-md transform flex-col overflow-hidden border border-slate-200/90 bg-slate-50 text-left align-middle shadow-xl transition-all max-sm:max-h-[100dvh] max-sm:min-h-0 max-sm:max-w-none max-sm:flex-1 max-sm:rounded-none max-sm:border-0 sm:max-h-[min(92vh,1200px)] sm:max-w-6xl lg:max-w-7xl sm:rounded-[2rem]">
                <div className="flex min-h-0 flex-1 flex-col sm:min-h-0">
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain bg-slate-50 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                    <div className="mb-5">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 shadow-sm ring-1 ring-slate-200/80">
                        {t("work_orders.billing_ready.eyebrow")}
                      </div>
                      <Dialog.Title
                        as="h3"
                        className="mt-3 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl"
                      >
                        {isReadyStatus
                          ? t("work_orders.billing_ready.title_edit")
                          : t("work_orders.billing_ready.title")}
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-slate-600">
                        {t("work_orders.billing_ready.intro")}
                      </p>
                    </div>

                    <Formik
                      enableReinitialize
                      initialValues={initialValues}
                      onSubmit={(values, { setSubmitting: fSet }) => {
                        fSet(false);
                      }}
                    >
                      {(formik) => (
                        <Form
                          onSubmit={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                              <div className="min-w-0 lg:col-span-5">
                                <OrderDialogSection
                                  title={t("work_orders.billing_ready.section_invoice")}
                                >
                                  <PMField
                                    name="invoice_title"
                                    type="text"
                                    label={t("Invoice Title")}
                                    {...formik}
                                  />
                                  <PMField
                                    multiline
                                    name="invoice_description"
                                    type="text"
                                    rows={5}
                                    label={t("Invoice Text")}
                                    {...formik}
                                  />
                                </OrderDialogSection>
                              </div>
                              <div className="min-w-0 space-y-6 lg:col-span-7">
                                <OrderDialogSection
                                  title={t("work_orders.billing_ready.section_hours")}
                                  description={t(
                                    "work_orders.billing_ready.section_hours_subtitle",
                                    {
                                      selected: formatHourDecimal(
                                        selectedTotalHours()
                                      ),
                                      total: formatHourDecimal(
                                        billableHours.reduce(
                                          (a, h) => a + Number(h.time || 0),
                                          0
                                        )
                                      ),
                                    }
                                  )}
                                  headerRight={
                                    <PMSwitch
                                      name="include_hours"
                                      hideLabel
                                      ariaLabel={t(
                                        "work_orders.billing_ready.toggle_hours_aria"
                                      )}
                                      onChange={() => {}}
                                      {...formik}
                                    />
                                  }
                                >
                                  {formik.values.include_hours && (
                                    <div className="space-y-4">
                                      {billableHours.length === 0 ? (
                                        <PMNotice
                                          severity="info"
                                          title=""
                                          description={t(
                                            "work_orders.billing_ready.no_billable_hours"
                                          )}
                                        />
                                      ) : (
                                        <div className="max-h-48 space-y-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                                          {billableHours.map((hour) => (
                                            <label
                                              key={hour.id}
                                              className="flex cursor-pointer items-center rounded-xl p-2 transition hover:bg-white"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={selectedHours.includes(
                                                  hour.id
                                                )}
                                                onChange={(e) => {
                                                  if (e.target.checked) {
                                                    setSelectedHours([
                                                      ...selectedHours,
                                                      hour.id,
                                                    ]);
                                                  } else {
                                                    setSelectedHours(
                                                      selectedHours.filter(
                                                        (id) => id !== hour.id
                                                      )
                                                    );
                                                  }
                                                }}
                                                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                              />
                                              <div className="ml-3 min-w-0 flex-1 text-left">
                                                <div className="text-sm font-medium text-slate-900">
                                                  {hour.displayTitle ||
                                                    hour.user?.displayTitle ||
                                                    `Time ${hour.id}`}
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                  {formatHourDecimal(hour.time)}{" "}
                                                  h
                                                </div>
                                              </div>
                                            </label>
                                          ))}
                                        </div>
                                      )}
                                      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                        <label className="mb-1 block text-sm text-slate-600">
                                          {t(
                                            "service.report.order_dialog.manual_hours_label"
                                          )}
                                        </label>
                                        <input
                                          type="number"
                                          step="0.5"
                                          min="0"
                                          value={
                                            hoursSettings.manualQuantity ===
                                            null
                                              ? ""
                                              : hoursSettings.manualQuantity
                                          }
                                          onChange={(e) =>
                                            setHoursSettings({
                                              ...hoursSettings,
                                              manualQuantity:
                                                e.target.value === ""
                                                  ? null
                                                  : parseFloat(e.target.value),
                                            })
                                          }
                                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                                          placeholder={t(
                                            "service.report.order_dialog.manual_hours_placeholder"
                                          )}
                                        />
                                        <label className="mb-1 block text-sm text-slate-600">
                                          {t(
                                            "service.report.order_dialog.hours_rounding_label"
                                          )}
                                        </label>
                                        <select
                                          value={
                                            hoursSettings.roundingMode ||
                                            "actual"
                                          }
                                          onChange={(e) =>
                                            setHoursSettings({
                                              ...hoursSettings,
                                              roundingMode: e.target.value,
                                            })
                                          }
                                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm"
                                        >
                                          <option value="actual">
                                            {t(
                                              "service.report.order_dialog.hours_rounding_actual"
                                            )}
                                          </option>
                                          <option value="ceil_half">
                                            {t(
                                              "service.report.order_dialog.hours_rounding_ceil_half"
                                            )}
                                          </option>
                                          <option value="nearest_half">
                                            {t(
                                              "service.report.order_dialog.hours_rounding_nearest_half"
                                            )}
                                          </option>
                                          <option value="ceil_hour">
                                            {t(
                                              "service.report.order_dialog.hours_rounding_ceil_hour"
                                            )}
                                          </option>
                                          <option value="nearest_hour">
                                            {t(
                                              "service.report.order_dialog.hours_rounding_nearest_hour"
                                            )}
                                          </option>
                                        </select>
                                        <label className="mb-1 block text-sm text-slate-600">
                                          {t(
                                            "service.report.order_dialog.hour_lines_label"
                                          )}
                                        </label>
                                        <select
                                          value={hoursSettings.hourLines || "each"}
                                          onChange={(e) =>
                                            setHoursSettings({
                                              ...hoursSettings,
                                              hourLines: e.target.value,
                                            })
                                          }
                                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm"
                                        >
                                          <option value="each">
                                            {t(
                                              "service.report.order_dialog.hour_lines_each"
                                            )}
                                          </option>
                                          <option value="grouped">
                                            {t(
                                              "service.report.order_dialog.hour_lines_grouped"
                                            )}
                                          </option>
                                        </select>
                                        <label className="mb-1 block text-sm text-slate-600">
                                          {t(
                                            "service.report.order_dialog.hours_discount_label"
                                          )}
                                        </label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          max="100"
                                          value={hoursSettings.discount}
                                          onChange={(e) =>
                                            setHoursSettings({
                                              ...hoursSettings,
                                              discount:
                                                parseFloat(e.target.value) ||
                                                0,
                                            })
                                          }
                                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm"
                                        />
                                        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-800">
                                          {t(
                                            "service.report.order_dialog.hours_preview_quantity",
                                            {
                                              hours:
                                                calculateHoursQuantity().toFixed(
                                                  1
                                                ),
                                            }
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </OrderDialogSection>

                                <OrderDialogSection
                                  title={t(
                                    "work_orders.billing_ready.section_items"
                                  )}
                                  description={t(
                                    "work_orders.billing_ready.section_items_subtitle",
                                    {
                                      selected: selectedItems.length,
                                      total: usageItems.length,
                                    }
                                  )}
                                  headerRight={
                                    <PMSwitch
                                      name="include_items"
                                      hideLabel
                                      ariaLabel={t(
                                        "work_orders.billing_ready.toggle_items_aria"
                                      )}
                                      onChange={() => {}}
                                      {...formik}
                                    />
                                  }
                                >
                                  {formik.values.include_items && (
                                    <div className="max-h-48 space-y-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                                      {usageItems.length === 0 ? (
                                        <p className="text-sm italic text-slate-500">
                                          {t(
                                            "work_orders.billing_ready.no_usage_items"
                                          )}
                                        </p>
                                      ) : (
                                        usageItems.map((item) => (
                                          <label
                                            key={item.id}
                                            className="flex cursor-pointer items-center rounded-xl p-2 transition hover:bg-white"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedItems.includes(
                                                item.id
                                              )}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setSelectedItems([
                                                    ...selectedItems,
                                                    item.id,
                                                  ]);
                                                } else {
                                                  setSelectedItems(
                                                    selectedItems.filter(
                                                      (id) => id !== item.id
                                                    )
                                                  );
                                                }
                                              }}
                                              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                            />
                                            <div className="ml-3 min-w-0 flex-1 text-left">
                                              <div className="text-sm font-medium text-slate-900">
                                                {item.title ||
                                                  item.product?.title ||
                                                  `Linje ${item.id}`}
                                              </div>
                                              <div className="text-xs text-slate-500">
                                                ×{item.quantity_used || 0}
                                              </div>
                                            </div>
                                          </label>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </OrderDialogSection>

                                <OrderDialogSection
                                  title={t(
                                    "work_orders.billing_ready.section_group"
                                  )}
                                >
                                  <PMSwitch
                                    name="add_tilleggsarbeider_group"
                                    label={t(
                                      "service.report.order_dialog.add_tilleggsarbeider_group_label"
                                    )}
                                    onChange={() => {}}
                                    {...formik}
                                  />
                                  <p className="mt-2 text-xs text-slate-600">
                                    {t(
                                      "work_orders.billing_ready.add_tilleggsarbeider_hint"
                                    )}
                                  </p>
                                </OrderDialogSection>
                              </div>
                            </div>

                            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                              <button
                                type="button"
                                className="order-3 inline-flex w-full justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 sm:order-1 sm:mr-auto sm:w-auto"
                                onClick={onClose}
                                disabled={submitting}
                              >
                                {t("Close")}
                              </button>
                              {!hasLinkedOrder ? (
                                <button
                                  type="button"
                                  disabled={submitting}
                                  className="order-1 inline-flex w-full justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50 sm:order-2 sm:w-auto"
                                  onClick={() =>
                                    handleSaveReadyOnly(formik.values)
                                  }
                                >
                                  {submitting
                                    ? t("report.sectionDialog.saving")
                                    : t("work_orders.billing_ready.submit")}
                                </button>
                              ) : null}
                              {!hasLinkedOrder ? (
                                <button
                                  type="button"
                                  disabled={submitting}
                                  className="order-2 inline-flex w-full justify-center rounded-xl border border-transparent bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50 sm:order-3 sm:w-auto"
                                  onClick={() =>
                                    handleCreateSalesOrder(formik.values)
                                  }
                                >
                                  {submitting
                                    ? t("report.sectionDialog.saving")
                                    : t(
                                        "work_orders.billing_ready.submit_create_order"
                                      )}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
